import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useContent } from "../../lib/content";
import { toast } from "sonner";
import { Save, RotateCcw } from "lucide-react";

// UI grouping for clarity — keys must match backend DEFAULT_SITE_CONTENT
const GROUPS = [
  {
    label: "Felső sáv (Topbar)",
    fields: [
      { key: "topbar_phone_label", label: "Telefon címke", placeholder: "HÍVJON" },
      { key: "topbar_phone", label: "Telefonszám" },
      { key: "topbar_notice", label: "Piros figyelmeztetés", textarea: true },
    ],
  },
  {
    label: "Fő fejléc",
    fields: [
      { key: "header_logo_initials", label: "Logó iniciálék (rövid)", hint: "Max 3 karakter" },
      { key: "header_company_name", label: "Cégnév" },
      { key: "header_company_subtitle", label: "Alcím" },
      { key: "header_price_period", label: "Aktuális ár-időszak felirat (1. sor)" },
      { key: "header_price_subtext", label: "Felhívás (2. sor)", textarea: true },
      { key: "header_price_subtext2", label: "Felhívás (3. sor)", textarea: true },
      { key: "akcio_label", label: "AKCIÓ pecsét szövege" },
    ],
  },
  {
    label: "Katalógus hero",
    fields: [
      { key: "catalog_hero_eyebrow", label: "Eyebrow (kis felirat)" },
      { key: "catalog_hero_title", label: "Főcím" },
      { key: "catalog_hero_subtitle", label: "Alcím" },
      { key: "catalog_hero_notice", label: "Piros figyelmeztetés a hero-ban" },
    ],
  },
  {
    label: "Bejelentkezés / Regisztráció",
    fields: [
      { key: "login_welcome_title", label: "Bejelentkezés főcím" },
      { key: "login_returning_title", label: "Visszatérő vendég szekció címe" },
      { key: "login_new_customer_title", label: "Új ügyfél szekció címe" },
      { key: "login_new_customer_body", label: "Új ügyfél leírás", textarea: true },
    ],
  },
  {
    label: "Pénznem (Currency)",
    fields: [
      {
        key: "currency_default",
        label: "Alapértelmezett pénznem (EUR vagy HUF)",
        hint: "Ezt fogja látni minden új látogató. Bárki átkapcsolhatja a navbar EUR/Ft kapcsolóval.",
      },
      {
        key: "currency_huf_rate",
        label: "EUR → HUF árfolyam",
        hint: "Pl. 400 (azaz 1 EUR = 400 Ft). Ez alapján számoljuk a forint árakat.",
      },
    ],
  },
  {
    label: "Árazás — beégetett árrés (%)",
    fields: [
      {
        key: "markup_bmw_percent",
        label: "BMW árrés (%)",
        hint: "Pl. 15 → minden BMW termék base_price = beszállítói ár × 1,15. Új feltöltéseknél azonnal érvényes, a meglévő készletre az „Aktív készletforrások” alatt található átszámító gomb hat.",
      },
      {
        key: "markup_mercedes_percent",
        label: "Mercedes árrés (%)",
        hint: "Pl. 15 → minden Mercedes (PKW + LKW) termék base_price = Preis × 1,15. Műhely-szintű customer árrés Mercedesre is letiltva.",
      },
    ],
  },
  {
    label: "Lábléc (Footer)",
    fields: [
      { key: "footer_text", label: "Lábléc szöveg" },
      { key: "footer_links", label: "Lábléc linkek" },
    ],
  },
  {
    label: "Nyitólap (Landing — / útvonal)",
    fields: [
      { key: "landing_company_name", label: "Cégnév (logó alatt + footer)" },
      { key: "landing_tagline", label: "Szolgáltatás slogan (kék felirat)" },
      { key: "landing_service_1", label: "1. szolgáltatás" },
      { key: "landing_service_2", label: "2. szolgáltatás" },
      { key: "landing_service_3", label: "3. szolgáltatás" },
      { key: "landing_service_4", label: "4. szolgáltatás" },
      { key: "landing_service_5", label: "5. szolgáltatás" },
      { key: "landing_service_6", label: "6. szolgáltatás" },
      { key: "landing_service_7", label: "7. szolgáltatás", hint: "Üresen hagyva eltűnik" },
      { key: "landing_address", label: "Cím" },
      { key: "landing_hours", label: "Nyitvatartás" },
      { key: "landing_phone", label: "Telefonszám" },
      { key: "landing_email", label: "E-mail cím" },
    ],
  },
];

export default function AdminContent() {
  const { refresh } = useContent();
  const [values, setValues] = useState({});
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/site-content");
      setValues(data.values || {});
      setDefaults(data.defaults || {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site-content", { values });
      toast.success("Szövegek elmentve");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const resetField = (key) => {
    setValues((v) => ({ ...v, [key]: defaults[key] || "" }));
  };

  const resetAll = async () => {
    if (!window.confirm("Visszaállítja az ÖSSZES szöveget az alapértelmezett értékekre? Ez nem vonható vissza.")) return;
    try {
      const { data } = await api.post("/admin/site-content/reset");
      setValues(data.values || {});
      toast.success("Alapértelmezett szövegek visszaállítva");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) {
    return <div className="card-elev p-8 text-center text-sm" style={{ color: "var(--text-mute)" }}>Betöltés...</div>;
  }

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
        <div>
          <h1 className="text-2xl font-bold uppercase tracking-wide">Tartalomkezelő</h1>
          <p className="text-sm" style={{ color: "var(--text-mute)" }}>
            Szerkesztheti az oldal összes statikus szövegét. A változtatások mentés után azonnal érvényesülnek.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={resetAll} className="btn-ghost" data-testid="reset-all-content-btn">
            <RotateCcw size={12} /> Összes alapértelmezett
          </button>
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-content-btn">
            <Save size={14} /> {saving ? "Mentés..." : "Mentés"}
          </button>
        </div>
      </div>
      <div className="border-b-2 mb-5" style={{ borderColor: "var(--brand)" }} />

      <div className="space-y-5">
        {GROUPS.map((g) => (
          <section key={g.label} className="card-elev overflow-hidden" data-testid={`content-section-${g.label}`}>
            <div className="section-title">{g.label}</div>
            <div className="p-5 space-y-4">
              {g.fields.map((f) => {
                const val = values[f.key] ?? "";
                const def = defaults[f.key] ?? "";
                const isDirty = val !== def;
                return (
                  <div key={f.key} className="grid sm:grid-cols-[220px_1fr] gap-3 sm:items-start">
                    <div>
                      <label className="text-sm font-bold block">{f.label}</label>
                      <div className="text-[10px] font-mono" style={{ color: "var(--text-disabled)" }}>{f.key}</div>
                      {f.hint && <div className="text-[10px]" style={{ color: "var(--text-mute)" }}>{f.hint}</div>}
                    </div>
                    <div>
                      {f.textarea ? (
                        <textarea
                          className="input"
                          rows={3}
                          value={val}
                          onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                          placeholder={f.placeholder || def}
                          data-testid={`content-field-${f.key}`}
                        />
                      ) : (
                        <input
                          className="input"
                          value={val}
                          onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                          placeholder={f.placeholder || def}
                          data-testid={`content-field-${f.key}`}
                        />
                      )}
                      {isDirty && (
                        <button
                          type="button"
                          onClick={() => resetField(f.key)}
                          className="text-[11px] mt-1 hover:underline"
                          style={{ color: "var(--brand)" }}
                          data-testid={`reset-field-${f.key}`}
                        >
                          ↺ Alapértelmezett: "{def}"
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <div className="mt-6 flex justify-end">
        <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-content-btn-bottom">
          <Save size={14} /> {saving ? "Mentés..." : "Mentés"}
        </button>
      </div>
    </div>
  );
}
